import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import LiveCounter from './LiveCounter';
// import Lottie from 'lottie-react';
// import loadingAnimation from '../Loading.lottie';

const GrammarChecker = () => {
  const [text, setText] = useState('');
  const [correctedText, setCorrectedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { currentUser, userProfile, updateDailyRequest } = useAuth();

  
  const guestLimit = 5;
  const signedUserLimit = 10;
  const today = new Date().toDateString();
  
  // Get guest requests from localStorage
  const getGuestRequests = () => {
    const stored = localStorage.getItem('guestRequests');
    if (stored) {
      const data = JSON.parse(stored);
      if (data.date === today) {
        return data.count;
      }
    }
    return 0;
  };
  
  const setGuestRequests = (count) => {
    localStorage.setItem('guestRequests', JSON.stringify({ date: today, count }));
  };
  
  const guestRequests = getGuestRequests();
  const isOverGuestLimit = !currentUser && guestRequests >= guestLimit;
  const isOverUserLimit = currentUser && userProfile && userProfile.lastRequestDate === today && (userProfile.dailyRequests || 0) >= signedUserLimit;

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(correctedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const handleFixGrammar = async () => {
    if (!currentUser && isOverGuestLimit) {
      setError(`Guest users have ${guestLimit} free requests per day. Sign up for ${signedUserLimit} free requests daily.`);
      return;
    }

    if (currentUser && isOverUserLimit) {
      setError(`You have reached your daily limit of ${signedUserLimit} requests.`);
      return;
    }

    if (!text.trim()) {
      setError('Please enter some text to check');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Try Gemini API first, fallback to LanguageTool if needed
      let correctedResult;
      try {
        correctedResult = await callGeminiAPI(text);
      } catch (geminiError) {
        console.log('Gemini API failed, trying LanguageTool...', geminiError);
        correctedResult = await callLanguageToolAPI(text);
      }
      setCorrectedText(correctedResult);
      
      // Save to Firebase for admin tracking
      try {
        await addDoc(collection(db, 'grammarFixes'), {
          originalText: text,
          correctedText: correctedResult,
          userId: currentUser ? currentUser.uid : 'guest',
          userEmail: currentUser ? currentUser.email : 'guest',
          timestamp: serverTimestamp(),
          characterCount: text.length
        });
        console.log('✅ Successfully saved to Firebase');
      } catch (error) {
        console.error('❌ Failed to save to Firebase:', error);
        console.error('Error details:', error.message);
      }
      
      // Update daily request count
      if (currentUser && updateDailyRequest) {
        await updateDailyRequest();
      } else if (!currentUser) {
        setGuestRequests(guestRequests + 1);
      }
      
    } catch (err) {
      setError('Failed to check grammar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const callLanguageToolAPI = async (text) => {
    try {
      console.log('Calling LanguageTool API with text:', text.substring(0, 100) + '...');
      
      // Split long text into chunks (LanguageTool has 5000 char limit for free tier)
      const chunkSize = 4000;
      const chunks = [];
      
      if (text.length <= chunkSize) {
        chunks.push(text);
      } else {
        // Split by sentences to maintain context
        const sentences = text.split(/([.!?]\s+)/);
        let currentChunk = '';
        
        for (let i = 0; i < sentences.length; i++) {
          if (currentChunk.length + sentences[i].length > chunkSize && currentChunk.length > 0) {
            chunks.push(currentChunk.trim());
            currentChunk = sentences[i];
          } else {
            currentChunk += sentences[i];
          }
        }
        if (currentChunk.trim()) {
          chunks.push(currentChunk.trim());
        }
      }
      
      let correctedChunks = [];
      
      for (const chunk of chunks) {
        const response = await fetch('https://api.languagetool.org/v2/check', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            text: chunk,
            language: 'en-US',
            enabledOnly: 'false'
          })
        });

        if (!response.ok) {
          throw new Error(`LanguageTool API request failed: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.matches && data.matches.length > 0) {
          let correctedChunk = chunk;
          
          // Apply corrections from end to start to maintain character positions
          const sortedMatches = data.matches.sort((a, b) => b.offset - a.offset);
          
          for (const match of sortedMatches) {
            if (match.replacements && match.replacements.length > 0) {
              const replacement = match.replacements[0].value;
              const start = match.offset;
              const end = match.offset + match.length;
              correctedChunk = correctedChunk.slice(0, start) + replacement + correctedChunk.slice(end);
            }
          }
          
          correctedChunks.push(correctedChunk);
        } else {
          correctedChunks.push(chunk);
        }
        
        // Add small delay between requests to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const finalText = correctedChunks.join(' ');
      console.log('LanguageTool corrected text:', finalText.substring(0, 200) + '...');
      return finalText;
      
    } catch (error) {
      console.error('LanguageTool API Error:', error);
      throw error;
    }
  };

  const callGeminiAPI = async (text) => {
    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if (!apiKey || apiKey === 'your_gemini_api_key_here') {
        console.error('API key not configured');
        throw new Error('API key not configured');
      }

      console.log('Calling Gemini API with text:', text.substring(0, 100) + '...');
      
      const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': apiKey,
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `You are an expert grammar checker and writing assistant. Your task is to carefully review and correct the following text.

Fix these issues:
- Grammar mistakes and sentence structure
- Spelling errors
- Punctuation problems
- Word choice and clarity
- Flow and readability

IMPORTANT RULES:
1. Return ONLY the corrected text, no explanations
2. Maintain the original meaning and tone
3. Keep the same paragraph structure
4. Do NOT add quotes, headers, or any extra formatting
5. If the text is already perfect, still return it (don't say "no changes needed")

Original text:
${text}`
            }]
          }],
          generationConfig: {
            temperature: 0.2,
            maxOutputTokens: 8192,
            topK: 40,
            topP: 0.95
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts && data.candidates[0].content.parts[0]) {
        let correctedText = data.candidates[0].content.parts[0].text.trim();
        
        // Remove any quotes that might have been added
        if ((correctedText.startsWith('"') && correctedText.endsWith('"')) || 
            (correctedText.startsWith("'") && correctedText.endsWith("'"))) {
          correctedText = correctedText.slice(1, -1);
        }
        
        // Check if Gemini actually made changes
        if (correctedText.toLowerCase().replace(/\s+/g, ' ') === text.toLowerCase().replace(/\s+/g, ' ')) {
          console.log('Gemini returned same text, trying LanguageTool...');
          throw new Error('No changes detected from Gemini');
        }
        
        console.log('Gemini corrected text:', correctedText.substring(0, 200) + '...');
        return correctedText;
      } else {
        console.error('Unexpected API response structure:', data);
        throw new Error('Invalid API response structure');
      }
    } catch (error) {
      console.error('Gemini API Error:', error);
      
      // Enhanced fallback correction
      const corrected = text
        .replace(/\bi\b/g, 'I')
        .replace(/\bteh\b/g, 'the')
        .replace(/\brecieve\b/g, 'receive')
        .replace(/\boccured\b/g, 'occurred')
        .replace(/\bexcellant\b/g, 'excellent')
        .replace(/\bwere\s+are\b/g, 'we are')
        .replace(/\byour\b(?=\s+(going|coming|doing))/g, "you're")
        .replace(/\bits\b(?=\s+(going|a|the))/g, "it's")
        .replace(/\bto\s+much\b/g, 'too much')
        .replace(/\bto\s+many\b/g, 'too many')
        .replace(/\bthere\s+is\s+many\b/g, 'there are many')
        .replace(/\bwho\s+is\s+they\b/g, 'who are they')
        .replace(/\.([A-Z])/g, '. $1')
        .replace(/([a-z])([A-Z])/g, '$1. $2')
        .replace(/\s+/g, ' ')
        .trim();
        
      console.log('Using fallback correction');
      return corrected;
    }
  };

  const canUseChecker = !isOverGuestLimit && !isOverUserLimit;
  
  return (
    <div className="grammar-checker">
      {!currentUser && (
        <div className="usage-stats">
          <div className="usage-info">
            Guest Mode - {guestRequests}/{guestLimit} requests used today
          </div>
          <div className="upgrade-prompt">
            <Link to="/signup">Sign up for {signedUserLimit} free requests daily</Link>
          </div>
        </div>
      )}

      {currentUser && userProfile && (
        <div className="usage-stats">
          <div className="usage-info">
            Requests used today: {(userProfile.dailyRequests || 0)} / {signedUserLimit}
          </div>
        </div>
      )}

      <div className="text-area-container" style={{ position: 'relative' }}>
        <textarea
          className="text-area"
          placeholder="Paste or type your text here to fix grammar and improve clarity..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={!canUseChecker}
        />
        <div className={`word-count`}>
          {text.length.toLocaleString()} characters
        </div>
        
        {loading && (
          <div className="loading-overlay">
            <div className="ai-loading-container">
              <div className="loader"></div>
            </div>
            <div className="loading-text">Fixing your grammar...</div>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message" style={{ textAlign: 'center', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div className="fix-button-container">
        <button 
          className="btn btn-primary btn-large"
          onClick={handleFixGrammar}
          disabled={loading || !canUseChecker}
        >
          {loading ? 'FIXING GRAMMAR...' : 'FIX GRAMMAR'}
        </button>
      </div>

      {correctedText && (
        <div className="text-area-container">
          <div className="corrected-text-header">
            <h3>Corrected Text:</h3>
            <button 
              className={`copy-button ${copied ? 'copied' : ''}`}
              onClick={handleCopyText}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="corrected-text-display">
            {correctedText}
          </div>
        </div>
      )}
      <LiveCounter />
    </div>
  );
};

export default GrammarChecker;
